// nativePlatform.js — Capacitor (iOS) native shell bootstrap.
//
// Mirrors the desktop shell's `window.chatroneDesktop` detection pattern: the
// web app stays identical, and native-only behavior is gated on isNative().
// On the web (no Capacitor) every export is a cheap no-op / false, so importing
// this is safe everywhere.
//
// Responsibilities:
//   - tag <html> with capacitor-native / capacitor-ios so CSS can add safe-area
//     insets and native-only tweaks;
//   - drive StatusBar + hide the splash once React has painted;
//   - expose a keyboard-height CSS var for inputs above the keyboard;
//   - handle deep links (chatrone://…) — the OAuth / password-reset return
//     path — by forwarding the path into React Router.
import { Capacitor } from '@capacitor/core'

export const isNative = () => Capacitor.isNativePlatform()
export const platform = () => Capacitor.getPlatform() // 'ios' | 'android' | 'web'
export const isIOS = () => Capacitor.getPlatform() === 'ios'

let _booted = false

// Route a deep-link URL into the SPA. OAuth/reset callbacks arrive as
// chatrone://<path> (or an https universal link); forward just the path+query
// to React Router via history + a popstate event (the same mechanism the
// desktop bridge uses for janet:summon-style events).
function routeDeepLink(url) {
  try {
    const u = new URL(url)
    // chatrone://oauth/gmail?… → pathname '/oauth/gmail'; universal link keeps its path.
    const path = (u.pathname || '/') + (u.search || '') + (u.hash || '')
    if (!path || path === '/') return
    window.history.pushState({}, '', path)
    window.dispatchEvent(new PopStateEvent('popstate'))
  } catch {
    /* malformed deep link — ignore */
  }
}

// Open an OAuth / connect flow. On native, Google (and others) reject OAuth
// inside a plain WKWebView ("disallowed_useragent"), so route it to the system
// browser via @capacitor/browser (SFSafariViewController — shares Safari's
// session, which Google accepts). Tokens land server-side regardless of where
// the browser runs (same as the desktop shell's externalization); the user
// taps Done to return and the connection shows on the next Settings load. On
// the web this is the unchanged full-page redirect.
export async function openConnectUrl(url) {
  if (!isNative()) {
    window.location.href = url
    return
  }
  // Relative /api/... must be absolutized for the system browser.
  const abs = url.startsWith('http') ? url : `https://chatrone-os.vercel.app${url}`
  const { Browser } = await import('@capacitor/browser')
  await Browser.open({ url: abs, presentationStyle: 'popover' }).catch(() => {
    window.location.href = abs
  })
}

// Call once from main.jsx after createRoot().render(). No-op on the web.
export async function initNativePlatform() {
  if (_booted || !isNative()) return
  _booted = true

  const root = document.documentElement
  root.classList.add('capacitor-native')
  if (isIOS()) root.classList.add('capacitor-ios')

  // Keep same-origin target="_blank" links INSIDE the app. Capacitor hands
  // every new-window request to the external browser, so links like "Open in
  // Creative Console" (a /console route) would launch Safari/Chrome instead of
  // navigating in place. We intercept only same-origin links here; external
  // links (Drive, Gmail, etc.) fall through and still open externally.
  document.addEventListener('click', (e) => {
    const a = e.target?.closest?.('a[target="_blank"]')
    if (!a) return
    const href = a.getAttribute('href')
    if (!href) return
    let url
    try { url = new URL(href, window.location.href) } catch { return }
    if (url.origin !== window.location.origin) return // external — let it open out
    e.preventDefault()
    window.history.pushState({}, '', url.pathname + url.search + url.hash)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, true)

  // Dynamic imports so the web bundle never eagerly loads native plugin code.
  const [{ StatusBar, Style }, { SplashScreen }, { Keyboard }, { App }] = await Promise.all([
    import('@capacitor/status-bar'),
    import('@capacitor/splash-screen'),
    import('@capacitor/keyboard'),
    import('@capacitor/app'),
  ])

  StatusBar.setStyle({ style: Style.Dark }).catch(() => {})

  // Expose keyboard height so fixed inputs (Ask Janet, compose) can lift.
  Keyboard.addListener('keyboardWillShow', (info) => {
    root.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`)
  }).catch(() => {})
  Keyboard.addListener('keyboardWillHide', () => {
    root.style.setProperty('--keyboard-height', '0px')
  }).catch(() => {})

  // Deep links (cold start + while running).
  App.addListener('appUrlOpen', ({ url }) => routeDeepLink(url)).catch(() => {})
  const launch = await App.getLaunchUrl().catch(() => null)
  if (launch?.url) routeDeepLink(launch.url)

  // On every return-to-foreground, check whether a newer web build has shipped
  // and reload the stale webview if so — the App 'resume' event is the reliable
  // foreground signal on iOS (visibilitychange can be flaky in WKWebView). This
  // is what stops a deployed fix from laying dormant until a force-quit.
  const { checkForShellUpdate } = await import('./shellUpdate')
  App.addListener('resume', () => checkForShellUpdate()).catch(() => {})

  // Push notifications: route taps, and keep this phone's APNs token saved.
  import('./nativePush').then(m => m.initNativePush()).catch(e => console.warn('[native] push init failed', e?.message))

  // Hide the splash once the app has actually painted (we set launchAutoHide
  // false in the config so it never flashes an empty webview).
  setTimeout(() => SplashScreen.hide().catch(() => {}), 300)
}
